// API service for Strategic Supplier Dependence data
// Now uses comprehensive multi-HS-code analysis for complete trade picture
// Use relative path to work with the same server as the frontend
import { API_BASE_URL } from './apiConfig';

const API_ROOT = `${API_BASE_URL}/api`;

/**
 * Get comprehensive supplier concentration analysis data using ALL HS codes
 * @param {string} mineral - The mineral to analyze
 * @param {string} flow - Import or Export (default: Import)
 * @returns {Promise<Object>} Comprehensive supplier concentration data with HHI, ENS, and stacked shares
 */
export const getSupplierConcentrationData = async (mineral, flow = 'Import') => {
  try {
    // Use legacy endpoint for frontend compatibility, but it now routes to comprehensive analysis
    const response = await fetch(`${API_ROOT}/time-series/supplier-concentration?mineral=${encodeURIComponent(mineral)}&flow=${encodeURIComponent(flow)}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Log comprehensive analysis info
    if (data.comprehensive_analysis) {
      console.log(`✅ Comprehensive analysis for ${mineral}: ${data.hs_codes_analyzed} HS codes analyzed`);
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching comprehensive supplier concentration data:', error);
    throw error;
  }
};

/**
 * Get detailed strategic supplier analysis (new comprehensive format)
 * @param {string} mineral - The mineral to analyze
 * @param {string} flow - Import or Export (default: Import)
 * @returns {Promise<Object>} Full strategic supplier analysis with risk assessment
 */
export const getStrategicSupplierAnalysis = async (mineral, flow = 'Import') => {
  try {
    const response = await fetch(`${API_ROOT}/strategic-supplier/analysis/${encodeURIComponent(mineral)}?flow=${encodeURIComponent(flow)}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log(`📊 Strategic analysis for ${mineral}: ${data.hs_codes_analyzed} HS codes, ${data.data_coverage?.total_suppliers} suppliers`);
    
    return data;
  } catch (error) {
    console.error('Error fetching strategic supplier analysis:', error);
    throw error;
  }
};

/**
 * Get concentration alerts when HHI exceeds threshold
 * @param {string} mineral - The mineral to check
 * @param {number} threshold - HHI threshold (default: 2500)
 * @param {string} flow - Import or Export (default: Import)
 * @returns {Promise<Object>} Concentration alerts and summary
 */
export const getConcentrationAlerts = async (mineral, threshold = 2500, flow = 'Import') => {
  try {
    const response = await fetch(`${API_ROOT}/concentration-alerts?mineral=${encodeURIComponent(mineral)}&threshold=${threshold}&flow=${encodeURIComponent(flow)}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching concentration alerts:', error);
    throw error;
  }
};

/**
 * Get available minerals for supplier concentration analysis
 * @returns {Promise<Array>} List of available minerals
 */
export const getAvailableSupplierMinerals = async () => {
  try {
    const response = await fetch(`${API_ROOT}/strategic-supplier/minerals`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.minerals || [];
  } catch (error) {
    console.error('Error fetching available minerals:', error);
    return ['lithium', 'cobalt', 'nickel', 'rare_earth_elements', 'graphite']; // Fallback
  }
};

/**
 * Get complete HS code information for a mineral
 * @param {string} mineral - The mineral to analyze
 * @returns {Promise<Object>} HS codes, descriptions, and coverage information
 */
export const getMineralHSCodes = async (mineral) => {
  try {
    const response = await fetch(`${API_ROOT}/strategic-supplier/hs-codes/${encodeURIComponent(mineral)}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log(`📋 HS Codes for ${mineral}: ${data.total_codes} codes analyzed`);
    
    return data;
  } catch (error) {
    console.error('Error fetching HS codes information:', error);
    throw error;
  }
};
